import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api.js';
import { useI18n } from '../../i18n/index.jsx';
import { Alert } from '../../components/ui.jsx';
import '../workspace/ws.css';

// /d/:token — a document sent to a customer (LOI, offer, quotation, NDA):
// view or download the PDF, then accept, decline or comment with a typed
// name (date, time and IP are recorded). No sign-in: the link is the key.
export default function DocView() {
  const { token } = useParams();
  const { t, lang, errorText } = useI18n();
  const [d, setD] = useState(null);
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api(`/docs/${token}`).then(setD).catch((e) => setError(errorText(e))), [token, errorText]);
  useEffect(() => { load(); }, [load]);
  async function respond(action) {
    setBusy(true); setError('');
    try { await api(`/docs/${token}/respond`, { method: 'POST', body: { action, name, comment } }); setDone(t(`docView.done.${action}`)); setComment(''); load(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }
  const when = (x) => (x ? new Date(x).toLocaleString(lang === 'en' ? 'en-CA' : 'fr-CA', { dateStyle: 'long', timeStyle: 'short' }) : '');
  if (!d) return <div className="docview">{error ? <Alert>{error}</Alert> : <p className="muted">{t('common.loading')}</p>}</div>;
  return (
    <div className="docview">
      <div>
        <p className="muted small" style={{ margin: 0 }}>{d.company}{d.sender ? ` — ${d.sender}` : ''}</p>
        <h1 style={{ margin: '4px 0' }}>{d.title}</h1>
        <p className="muted">{t('docView.ref', { ref: d.ref })}{d.validUntil ? ` · ${t('docView.validUntil', { date: new Date(`${d.validUntil}T12:00:00`).toLocaleDateString(lang === 'en' ? 'en-CA' : 'fr-CA', { dateStyle: 'long' }) })}` : ''}</p>
        <span className={`ws-badge ws-doc-${d.status}`}>{t(`ws.doc.${d.status}`)}</span>
      </div>
      <iframe key={d.status} src={`/api/docs/${token}/pdf?v=${d.status}`} title={d.title} />
      <p><a className="btn btn-ghost" href={`/api/docs/${token}/pdf`} download={`${d.ref}.pdf`}>{t('docView.download')}</a></p>
      {done && <p className="callout callout-success" role="status">{done}</p>}
      {d.respondedAt && <p className="callout callout-info">{t(`docView.answered.${d.status}`, { name: d.responseName, date: when(d.respondedAt) })}</p>}
      {d.status === 'expired' && <p className="callout callout-warning">{t('docView.expired')}</p>}
      <section className="ws-card">
        <h2>{d.canRespond ? t('docView.answerTitle') : t('docView.commentTitle')}</h2>
        <label className="field"><span className="field-label">{t('docView.name')}</span><input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" maxLength={120} /></label>
        <label className="field"><span className="field-label">{t('docView.comment')}</span><textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} /></label>
        {d.canRespond && <label className="field-checkbox"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /><span>{t('docView.agree')}</span></label>}
        <Alert>{error}</Alert>
        <div className="ws-actions">
          <button type="button" className="btn btn-ghost" disabled={busy || name.trim().length < 3 || !comment.trim()} onClick={() => respond('comment')}>{t('docView.sendComment')}</button>
          {d.canRespond && <button type="button" className="btn btn-ghost" disabled={busy || name.trim().length < 3} onClick={() => respond('decline')}>{t('docView.decline')}</button>}
          {d.canRespond && <button type="button" className="btn btn-primary" disabled={busy || name.trim().length < 3 || !agree} onClick={() => respond('accept')}>{t('docView.accept')}</button>}
        </div>
        <p className="ws-hint">{t('docView.legal')}</p>
      </section>
      {d.comments.length > 0 && (
        <section className="ws-card"><h2>{t('docView.comments')}</h2>
          <ul className="ws-list">{d.comments.map((c, i) => <li key={i}><span className="ws-grow"><strong>{c.name}</strong> — {c.body}</span><span className="small muted">{when(c.at)}</span></li>)}</ul></section>
      )}
    </div>
  );
}
